import { useTranslation } from 'react-i18next';

import type { useServiceWorker } from '@/offline/useServiceWorker.ts';

/**
 * The other half of `registerType: 'prompt'`.
 *
 * The new worker is installed and waiting; this asks before applying it. An
 * automatic reload here would discard a half-entered weight, which is the one
 * thing the app must never do — the user is holding a puppy and cannot easily
 * type it twice.
 *
 * It takes the worker rather than calling `useServiceWorker` itself, and that is
 * load-bearing: calling the hook is what REGISTERS the worker, so a component
 * rendered only on some screens would register the offline shell only on those
 * screens. It is called once in __root.tsx, above every gate.
 */
export const UpdateBar = ({ worker }: { worker: ReturnType<typeof useServiceWorker> }) => {
  const { t } = useTranslation();
  const { needRefresh, update, dismiss } = worker;

  if (!needRefresh) return null;

  return (
    <div className="flex items-center justify-between gap-3 bg-brand-600 px-4 py-2 text-sm text-white">
      <span>{t('update.available')}</span>
      <span className="flex gap-2">
        <button
          type="button"
          onClick={update}
          className="font-semibold underline transition-opacity hover:opacity-80"
        >
          {t('update.reload')}
        </button>
        <button
          type="button"
          onClick={dismiss}
          aria-label={t('common.cancel')}
          className="transition-opacity hover:opacity-80"
        >
          ✕
        </button>
      </span>
    </div>
  );
};
